// CompanyRelationForm.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Container,
  TextField,
  Autocomplete,
  Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';

// Mobile-friendly Select with search
const SearchableSelect = ({
  id,
  value,
  onChange,
  onBlur,
  label,
  options,
  error,
  touched,
  helperText,
  disabled = false,
  placeholder = 'Search...',
  getOptionLabel = (option) => option.label || option,
  getOptionValue = (option) => option.value || option,
  noOptionsText = 'No options available',
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const filteredOptions = options.filter(option => {
    const label = getOptionLabel(option).toLowerCase();
    return label.includes(searchTerm.toLowerCase());
  });

  const selectedOption = options.find(opt => getOptionValue(opt) === value) || null;

  const handleSelectChange = (event, newValue) => {
    const newId = newValue ? getOptionValue(newValue) : '';
    onChange({
      target: {
        value: newId,
      },
    });
    setSearchTerm('');
    setOpen(false);
  };

  const handleInputChange = (event, newInputValue) => {
    setSearchTerm(newInputValue);
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur({
        target: {
          value: value,
        },
      });
    }
    if (isMobile) {
      setTimeout(() => setOpen(false), 300);
    }
  };

  return (
    <Autocomplete
      id={id}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setSearchTerm('');
      }}
      value={selectedOption}
      onChange={handleSelectChange}
      onInputChange={handleInputChange}
      options={filteredOptions}
      getOptionLabel={getOptionLabel}
      disabled={disabled}
      loading={options.length === 0}
      loadingText="Loading..."
      noOptionsText={noOptionsText}
      clearOnEscape
      blurOnSelect
      handleHomeEndKeys
      isOptionEqualToValue={(option, value) => {
        return getOptionValue(option) === getOptionValue(value);
      }}
      renderOption={(props, option) => {
        const isSelf = value && getOptionValue(option) === value;
        return (
          <li {...props} style={{ 
            fontSize: '0.875rem', 
            padding: '8px 16px',
            backgroundColor: isSelf ? '#e3f2fd' : 'transparent',
          }}>
            {getOptionLabel(option)}
            {isSelf && (
              <Chip 
                label="Self" 
                size="small" 
                color="info" 
                sx={{ ml: 1 }} 
              />
            )}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required
          error={!!error && touched}
          helperText={touched && helperText ? helperText : ''}
          placeholder={isMobile ? placeholder : ''}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {params.InputProps.endAdornment}
                {value && (
                  <Chip
                    label="Clear"
                    size="small"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => {
                      onChange({
                        target: { value: '' }
                      });
                      setSearchTerm('');
                    }}
                    sx={{ ml: 1 }}
                  />
                )}
              </>
            ),
          }}
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: true,
          }}
        />
      )}
      disablePortal={!isMobile}
      popupIcon={isMobile ? <SearchIcon /> : undefined}
      sx={{
        '& .MuiAutocomplete-popupIndicator': {
          transform: 'rotate(0deg)',
        },
        '& .MuiAutocomplete-clearIndicator': {
          display: 'none',
        },
        '& .MuiAutocomplete-inputRoot': {
          paddingRight: '32px !important',
          '& .MuiAutocomplete-input': {
            padding: isMobile ? '10px 4px' : '8px 4px',
            fontSize: isMobile ? '16px' : '0.875rem',
          },
        },
        '& .MuiAutocomplete-popper': {
          '& .MuiAutocomplete-listbox': {
            maxHeight: isMobile ? '300px' : '200px',
            '& li': {
              padding: isMobile ? '12px 16px' : '8px 16px',
              fontSize: isMobile ? '16px' : '0.875rem',
              minHeight: isMobile ? '44px' : '32px',
            },
          },
        },
      }}
    />
  );
};

const CompanyRelationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [existingRelations, setExistingRelations] = useState([]);
  
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  
  const [formData, setFormData] = useState({
    companyID: '',
    relativeID: '',
    isActive: true,
  });

  const isEdit = !!id;

  useEffect(() => {
    fetchCompanies();
    fetchAllCompanies();
    fetchExistingRelations();
  }, []);

  useEffect(() => {
    if (isEdit) {
      fetchRelation();
    } else if (location.state?.companyId) {
      setFormData(prev => ({
        ...prev,
        companyID: location.state.companyId
      }));
    }
  }, [id, location.state]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/Companies/active-agencies');
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError('Failed to load companies. Please try again.');
    }
  };

  const fetchAllCompanies = async () => {
    try {
      const response = await api.get('/CompanyRelations/relatives');
      setAllCompanies(response.data || []);
    } catch (error) {
      console.error('Error fetching all companies:', error);
      setError('Failed to load companies. Please try again.');
    }
  };

  const fetchExistingRelations = async () => {
    try {
      const response = await api.get('/CompanyRelations');
      setExistingRelations(response.data || []);
    } catch (error) {
      console.error('Error fetching existing relations:', error);
    }
  };

  const fetchRelation = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(`/CompanyRelations/${id}`);
      const data = response.data;
      setFormData({
        companyID: data.companyID || '',
        relativeID: data.relativeID || '',
        isActive: data.isActive !== undefined ? data.isActive : true,
      });
    } catch (error) {
      console.error('Error fetching relation:', error);
      setError('Failed to load relation data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const requiredFields = {
    companyID: 'Company is required',
    relativeID: 'Relative Company is required',
  };

  const validateField = (field, value) => {
    if (requiredFields[field] && !value) {
      return requiredFields[field];
    }
    return null;
  };

  const validateForm = () => {
    const errors = {};
    const touched = {};
    let hasErrors = false;

    Object.keys(requiredFields).forEach(field => {
      touched[field] = true;
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
        hasErrors = true;
      }
    });

    // Check for duplicate relation (excluding current if editing)
    if (formData.companyID && formData.relativeID) {
      const isDuplicate = existingRelations.some(relation => {
        // Skip checking against itself when editing
        if (isEdit && relation.relationID === id) {
          return false;
        }
        return relation.companyID === formData.companyID && 
               relation.relativeID === formData.relativeID;
      });

      if (isDuplicate) {
        errors.relativeID = 'This relation already exists. Please choose a different combination.';
        hasErrors = true;
      }
    }

    setValidationErrors(errors);
    setTouchedFields(touched);
    return !hasErrors;
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleBlur = (field) => (event) => {
    setTouchedFields(prev => ({
      ...prev,
      [field]: true
    }));
    
    const error = validateField(field, formData[field]);
    if (error) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleSubmit = async () => {
    const isValid = validateForm();
    
    if (!isValid) {
      const firstErrorField = Object.keys(validationErrors)[0];
      if (firstErrorField) {
        const element = document.getElementById(`field-${firstErrorField}`);
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }, 300);
        }
      }
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const submitData = {
        companyID: formData.companyID,
        relativeID: formData.relativeID,
        isActive: formData.isActive,
      };

      if (isEdit) {
        submitData.relationID = id;
        await api.put(`/CompanyRelations/${id}`, submitData);
      } else {
        await api.post('/CompanyRelations', submitData);
      }

      navigate('/company/relations');
    } catch (error) {
      console.error('Error saving relation:', error);
      if (error.response) {
        if (error.response.status === 409) {
          setError('This company relation already exists. Please choose a different combination.');
        } else if (error.response.status === 400) {
          const message = error.response.data || '';
          setError(`Failed to save relation: ${message}`);
        } else {
          setError(`Failed to save relation: ${error.response.data || error.response.statusText}`);
        }
      } else {
        setError('Failed to save relation. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/company/relations');
  };

  const isSelfRelation = formData.companyID && formData.relativeID && formData.companyID === formData.relativeID;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ p: { xs: 1, sm: 3 } }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1" fontSize={{ xs: '1.2rem', sm: '1.5rem' }}>
            {isEdit ? 'Edit Relation' : 'Create New Relation'}
          </Typography>
          <Box display={{ xs: 'none', sm: 'block' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={isSaving}
              sx={{ mr: 1 }}
              size="small"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              size="small"
            >
              Cancel
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Self-Relation Warning (if applicable) */}
        {isSelfRelation && (
          <Alert severity="info" sx={{ mb: 3 }} icon={<InfoIcon />}>
            <Typography variant="body2">
              <strong>Self-Relation:</strong> You are relating this company to itself. 
              This is allowed, but please ensure this is what you intend to do.
            </Typography>
          </Alert>
        )}

        {/* Form */}
        <Card sx={{ bgcolor: '#fafafa' }}>
          <CardContent>
            <Grid container spacing={3} direction="column">
              {/* Company */}
              <Grid item xs={12}>
                <SearchableSelect
                  id="field-companyID"
                  value={formData.companyID}
                  onChange={handleChange('companyID')}
                  onBlur={handleBlur('companyID')}
                  label="Company"
                  options={companies}
                  getOptionLabel={(company) => `${company.companyName} - ${company.country || 'N/A'}`}
                  getOptionValue={(company) => company.companyID}
                  error={validationErrors.companyID}
                  touched={touchedFields.companyID}
                  helperText={validationErrors.companyID}
                  placeholder="Search for a company..."
                  noOptionsText="No companies found"
                />
              </Grid>

              {/* Relative Company */}
              <Grid item xs={12}>
                <SearchableSelect
                  id="field-relativeID"
                  value={formData.relativeID}
                  onChange={handleChange('relativeID')}
                  onBlur={handleBlur('relativeID')}
                  label="Relative Company"
                  options={allCompanies}
                  getOptionLabel={(company) => {
                    const isSelf = formData.companyID && company.companyID === formData.companyID;
                    return `${company.companyName} - ${company.country || 'N/A'}${isSelf ? ' (Self)' : ''}`;
                  }}
                  getOptionValue={(company) => company.companyID}
                  error={validationErrors.relativeID}
                  touched={touchedFields.relativeID}
                  helperText={validationErrors.relativeID}
                  placeholder="Search for a relative company..."
                  noOptionsText="No companies found"
                />
              </Grid>

              {/* Is Active Switch */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleChange('isActive')}
                      color="primary"
                      size="small"
                    />
                  }
                  label={formData.isActive ? 'Active' : 'Inactive'}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Info message */}
        <Box sx={{ mt: 2, p: 2, bgcolor: isSelfRelation ? '#fff3e0' : '#e3f2fd', borderRadius: 1 }}>
          <Typography variant="body2" color="textSecondary" fontSize="0.8rem">
            <strong>Note:</strong> A company relation links two companies together. 
            Each company can have multiple relations, but each combination of company 
            and relative company must be unique.
            {isSelfRelation ? (
              <span style={{ color: '#e65100' }}>
                {' '}⚠️ Self-relation detected - this company is being related to itself.
              </span>
            ) : (
              <span style={{ color: '#0d47a1' }}>
                {' '}✓ Self-relations are allowed.
              </span>
            )}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box 
          display="flex" 
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="center" 
          gap={2} 
          mt={3} 
          pt={2} 
          borderTop={1} 
          borderColor="divider"
        >
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={isSaving}
            fullWidth={true}
            sx={{ 
              py: { xs: 1.5, sm: 1 },
              fontSize: { xs: '1rem', sm: '0.9375rem' },
              maxWidth: { sm: '130px' },
              borderRadius: 2,
              '&:active': {
                transform: 'scale(0.98)',
              },
            }}
          >
            {isSaving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            fullWidth={true}
            sx={{ 
              py: { xs: 1.5, sm: 1 },
              fontSize: { xs: '1rem', sm: '0.9375rem' },
              maxWidth: { sm: '130px' },
              borderRadius: 2,
              '&:active': {
                transform: 'scale(0.98)',
              },
            }}
          >
            Cancel
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default CompanyRelationForm;